import { CanvasEx } from '@/typings/canvas';
import { ElementsGroup } from '@/typings/customilyElements/elementsGroup';
import { BaseCanvasElement } from '@/typings/customilyElements/canvasElements';
import type { CanvasElement } from '@/typings/customilyElements/canvasElements';

export default class GroupManager {
    private canvas: CanvasEx;
    private groups: ElementsGroup[] = [];
    private map = new Map<string, ElementsGroup>();
    private children = new Map<string, ElementsGroup[]>();

    constructor(canvas: CanvasEx) {
        this.canvas = canvas;
        this.refresh();
    }

    refresh(): void {
        this.groups = this.canvas.getElementsGroups() || [];
        this.map.clear();
        this.children.clear();

        for (const g of this.groups) {
            this.map.set(g.id, g);
            if (g.parentId) {
                const arr = this.children.get(g.parentId) || [];
                arr.push(g);
                this.children.set(g.parentId, arr);
            }
        }
    }

    getGroup(id: string): ElementsGroup | undefined {
        return this.map.get(id);
    }

    getAncestors(id: string): ElementsGroup[] {
        const result: ElementsGroup[] = [];
        let current = this.map.get(id);
        const visited = new Set<string>();
        while (current && current.parentId && !visited.has(current.parentId)) {
            visited.add(current.parentId);
            const parent = this.map.get(current.parentId);
            if (!parent) break;
            result.push(parent);
            current = parent;
        }
        return result;
    }

    getDescendants(id: string): ElementsGroup[] {
        const result: ElementsGroup[] = [];
        const stack = [...(this.children.get(id) || [])];
        while (stack.length) {
            const node = stack.pop() as ElementsGroup;
            result.push(node);
            const childs = this.children.get(node.id) || [];
            for (const c of childs) stack.push(c);
        }
        return result;
    }

    getAllGroups(): ElementsGroup[] {
        return this.groups;
    }

    getElements(group: ElementsGroup): CanvasElement[] {
        return this.canvas.getGroupElements(group) || [];
    }

    private getDirectPageEpsId(groupOrId: ElementsGroup | string): number | null {
        const group = typeof groupOrId === 'string' ? this.map.get(groupOrId) : this.map.get(groupOrId.id) || groupOrId;
        if (!group) return null;

        if (group.pageEpsId !== null && group.pageEpsId !== undefined) {
            return group.pageEpsId;
        }

        const pageEpsId = this.getElements(group).find(
            (element) => element?.pageEpsId !== null && element?.pageEpsId !== undefined
        )?.pageEpsId;

        return pageEpsId ?? null;
    }

    assignResolvedPageEpsIdToGroupsList(groups: ElementsGroup[]): void {
        if (!groups?.length) return;

        const groupsById = new Map(groups.map((group) => [group.id, group]));
        const childrenByParentId = new Map<string, ElementsGroup[]>();

        for (const group of groups) {
            if (!group.parentId) continue;

            const children = childrenByParentId.get(group.parentId) || [];
            children.push(group);
            childrenByParentId.set(group.parentId, children);
        }

        const getDescendantPageEpsIdFromList = (groupId: string, visited = new Set<string>()): number | null => {
            if (visited.has(groupId)) return null;
            visited.add(groupId);

            const children = childrenByParentId.get(groupId) || [];

            for (const child of children) {
                const childPageEpsId = this.getDirectPageEpsId(child);
                if (childPageEpsId !== null && childPageEpsId !== undefined) {
                    return childPageEpsId;
                }

                const descendantPageEpsId = getDescendantPageEpsIdFromList(child.id, visited);
                if (descendantPageEpsId !== null && descendantPageEpsId !== undefined) {
                    return descendantPageEpsId;
                }
            }

            return null;
        };

        const getAncestorPageEpsIdFromList = (group: ElementsGroup): number | null => {
            let currentParentId = group.parentId;
            const visited = new Set<string>();

            while (currentParentId && !visited.has(currentParentId)) {
                visited.add(currentParentId);

                const parentGroup = groupsById.get(currentParentId);
                if (!parentGroup) return null;

                const parentPageEpsId = this.getDirectPageEpsId(parentGroup);
                if (parentPageEpsId !== null && parentPageEpsId !== undefined) {
                    return parentPageEpsId;
                }

                currentParentId = parentGroup.parentId || null;
            }

            return null;
        };

        for (const group of groups.sort((a, b) => {
            if (a.parentId && !b.parentId) return -1;
            if (!a.parentId && b.parentId) return 1;
            return 0;
        })) {
            const directPageEpsId = this.getDirectPageEpsId(group);
            const descendantPageEpsId = getDescendantPageEpsIdFromList(group.id);
            const ancestorPageEpsId = getAncestorPageEpsIdFromList(group);

            group.pageEpsId = directPageEpsId ?? descendantPageEpsId ?? ancestorPageEpsId ?? null;
        }
    }

    private markElementsDirty(group: ElementsGroup, silent?: boolean): void {
        const elements: BaseCanvasElement[] = this.getElements(group);
        for (const el of elements) {
            el.set('dirty', true);
            if (!silent) {
                this.canvas.fire('object:modified', { target: el });
            }
        }
    }

    /**
     * Optimized visibility/lock flow:
     * - Resolve valid groups
     * - If `visible === false` hide and lock all groups
     * - If `visible === true` build the set of visible groups
     *   (selected + ancestors + descendants) and apply changes
     *   in a single pass to avoid repeated operations.
     */
    setVisibility(inputGroups: (ElementsGroup | string)[], visible: boolean): void {
        this.refresh();
        const allGroups = this.groups;

        // Resolve to ElementsGroup objects
        const validGroups: ElementsGroup[] = inputGroups
            .map((g) => (typeof g === 'string' ? this.map.get(g) : this.map.get((g as ElementsGroup).id) || undefined))
            .filter((g): g is ElementsGroup => !!g);

        if (validGroups.length === 0) return;

        // If asked to hide the selection, only hide/lock the requested groups.
        if (!visible) {
            for (const group of validGroups) {
                if (group.visible !== false || group.locked !== true) {
                    group.visible = false;
                    group.locked = true;
                    this.markElementsDirty(group);
                }
            }
            this.canvas.requestRenderAll();
            return;
        }

        // visible === true: build the final set of visible groups
        const visibleSet = new Set<string>();
        for (const group of validGroups) {
            visibleSet.add(group.id);
            const ancestors = this.getAncestors(group.id);
            for (const anc of ancestors) visibleSet.add(anc.id);
            const descendants = this.getDescendants(group.id);
            for (const d of descendants) visibleSet.add(d.id);
        }

        // If any of the selections is a layout, then apply hiding/lock to the rest.
        const hideOthers = validGroups.some((g) => !!g.isLayout);

        for (const g of allGroups) {
            const shouldBeVisible = visibleSet.has(g.id);
            if (shouldBeVisible) {
                if (g.visible !== true || g.locked !== false) {
                    g.visible = true;
                    g.locked = false;
                    this.markElementsDirty(g);
                }
            } else if (hideOthers) {
                if (g.isLayout === true) {
                    // hide layout groups that are not in the visible set
                    if (g.visible !== false || g.locked !== true) {
                        g.visible = false;
                        g.locked = true;
                        this.markElementsDirty(g);
                    }
                } else {
                    // Non-layout groups: only hide if they have a layout ancestor that will be hidden.
                    const ancestors = this.getAncestors(g.id);
                    const hiddenLayoutAncestor = ancestors.find((a) => !!a.isLayout && !visibleSet.has(a.id));
                    if (hiddenLayoutAncestor) {
                        if (g.visible !== false || g.locked !== true) {
                            g.visible = false;
                            g.locked = true;
                            this.markElementsDirty(g);
                        }
                    } else {
                        continue;
                    }
                }
            }
        }

        this.canvas.requestRenderAll();
    }

    /**
     * Marks all groups as visible and unlocked.
     */
    setAllVisibleAndUnlocked(): void {
        this.refresh();
        for (const g of this.groups) {
            g.visible = true;
            g.locked = false;
            this.markElementsDirty(g);
        }
        this.canvas.requestRenderAll();
    }

    /**
     * Returns groups that contain at least one element on the canvas.
     */
    getNonEmptyGroups(): ElementsGroup[] {
        this.refresh();
        return this.groups.filter((g) => {
            const els = this.getElements(g) || [];
            return els.length > 0;
        });
    }

    /**
     * Returns filtered and sorted groups.
     * @param type optional, filters by `group.type`
     * @param includeEmpty if false, filters out groups without elements
     * @param desc if true, sorts by `zIndex` descending
     */
    getGroupsSorted(type?: string, includeEmpty = true, desc = true): ElementsGroup[] {
        this.refresh();
        let list = this.groups.slice();
        if (type) list = list.filter((g) => g.type === type);
        if (!includeEmpty) list = list.filter((g) => (this.getElements(g) || []).length > 0);
        list.sort((a, b) => (desc ? b.zIndex - a.zIndex : a.zIndex - b.zIndex));
        return list;
    }

    /**
     * Checks whether all groups (optionally filtered by type/includeEmpty)
     * have a valid `position`. Returns `true` if there are no groups or
     * if every `position` is non-zero and not null.
     */
    areGroupsPositionsValid(type?: string, includeEmpty = true): boolean {
        const groups = this.getGroupsSorted(type, includeEmpty);
        if (groups && groups.length > 0) {
            return groups.map((g) => g.position).every((p) => p != 0 && p != null);
        }
        return true;
    }

    /**
     * Assigns missing positions to groups. Mirrors the original controller logic:
     * - obtains the list of groups (optionally excluding empty ones)
     * - computes starting index as max(position) + 1
     * - assigns incremental `position` to groups missing one
     */
    assignMissingPositions(includeEmpty = true): void {
        const groups = this.getGroupsSorted(undefined, includeEmpty);
        if (!groups || groups.length === 0) return;
        const maxPos = Math.max(...groups.map((g) => g.position || 0));
        let index = maxPos + 1;
        let changed = false;
        for (const group of groups) {
            if (!group.position) {
                group.position = index;
                index++;
                changed = true;
            }
        }
        if (changed) this.canvas.requestRenderAll();
    }
}
